import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import 'boxicons';
import { Button } from 'reactstrap';
import { useAuth } from 'hooks/useAuth';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { MoonLoader } from 'react-spinners';

import { TaskSummmaryLayout } from './styles';
import ButtonFilter from 'pages/guideline/components/Button-Filter';
import { GDriveIcon } from 'assets/icons';
import { handleDownloadGuidelineCSV, handleDownloadTaskCSV } from 'utils';

const TasksSummary = ({
  board,
  toggleFilter,
  filterTotal,
  isContent,
  isGuideline,
  show,
  userStatus,
}) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const accountId = user?.account;
  const firebaseHelper = getFirebaseBackend();
  const [gDriveContentLink, setGDriveContentLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const canDownloadTaskCSV = () => {
    return ['Super Admin', 'Admin', 'Client Admin'].includes(userStatus);
  };

  const canDownloadGuidelineCSV = () => {
    return ['Super Admin', 'Admin'].includes(userStatus);
  };

  const canCreate = () => {
    return ['Super Admin', 'Admin'].includes(userStatus);
  };

  const canOpenDrive = () => {
    return ['Super Admin', 'Admin'].includes(userStatus);
  };

  let tasksTotal = 0;

  let waitingTotal = 0;
  let readyTotal = 0;
  let impedimentTotal = 0;
  let developmentTotal = 0;
  let implementedTotal = 0;

  const countTotals = [
    () => {
      waitingTotal++;
    },
    () => {
      readyTotal++;
    },
    () => {
      impedimentTotal++;
    },
    () => {
      developmentTotal++;
    },
    () => {
      implementedTotal++;
    },
  ];

  // Counts tasks totals
  board?.columns.forEach((column) => {
    column.cards.forEach((task) => {
      if (task.status) {
        countTotals[task.status]();
      }
      tasksTotal++;
    });
  });

  const handleToggle = () => toggleFilter((prevState) => !prevState);

  const getGDriveLink = async () => {
    const account = await firebaseHelper.getAccount(accountId);
    if (account?.gDriveFolders) {
      isGuideline
        ? setGDriveContentLink(account?.gDriveFolders?.content?.root)
        : setGDriveContentLink(account?.gDriveFolders?.seo?.root);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      setIsLoading(true);
      isGuideline
        ? await handleDownloadGuidelineCSV(accountId)
        : await handleDownloadTaskCSV(accountId);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getGDriveLink();
  }, [accountId]);

  return (
    <TaskSummmaryLayout className="header-wrapper" style={{ display: show ? 'flex' : 'none' }}>
      {isGuideline ? (
        <div className="task-summary-guideline">
          <div className="task-summary-stats">
            <div className="task-summary-stat">
              <p className="task-summary-title">{translate('Total Guidelines')}</p>
              <p className="task-summary-number">{tasksTotal}</p>
            </div>
            {board.columns.slice(1, 8).map((column) => {
              return (
                <div className="task-summary-stat" key={column.id}>
                  <p className="task-summary-title">{translate(column.title)}</p>
                  <p className="task-summary-number">{column.cards.length}</p>
                </div>
              );
            })}
          </div>
          <div className="task-summary-btns">
            {canOpenDrive() && (
              <Button
                className="btn-drive"
                onClick={() => window.open(gDriveContentLink, '_blank')}
              >
                <GDriveIcon />
                {`${translate('Open')} Drive`}
              </Button>
            )}
            {canDownloadGuidelineCSV() && (
              <Button className="btn-download-csv" onClick={handleDownloadCSV}>
                {isLoading ? (
                  <MoonLoader size={20} color="#2d3748" loading={isLoading} />
                ) : (
                  <>
                    <i className="bx bx-download" />
                    {translate('Export CSV')}
                  </>
                )}
              </Button>
            )}
            <ButtonFilter handleToggle={handleToggle} filterTotal={filterTotal} />
            {canCreate() && (
              <Button
                className="btn-guideline-create"
                onClick={() => navigate('/create-guideline')}
              >
                {translate('Create Guideline')}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.99834 2C3.99834 3.47337 2.80394 4.66778 1.33057 4.66778C2.80394 4.66778 3.99834 5.86218 3.99834 7.33556C3.99834 5.86218 5.19275 4.66778 6.66612 4.66778C5.19275 4.66778 3.99834 3.47337 3.99834 2Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.3347 10.0054C11.3347 8.16367 9.84172 6.67066 8 6.67066C9.84172 6.67066 11.3347 5.17765 11.3347 3.33594C11.3347 5.17765 12.8277 6.67066 14.6694 6.67066C12.8277 6.67066 11.3347 8.16367 11.3347 10.0054Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.66607 9C6.66607 10.4734 5.47166 11.6678 3.99829 11.6678C5.47166 11.6678 6.66607 12.8622 6.66607 14.3356C6.66607 12.8622 7.86047 11.6678 9.33385 11.6678C7.86047 11.6678 6.66607 10.4734 6.66607 9Z"
                    fill="white"
                  />
                </svg>
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="task-summary-guideline">
          <div className="task-summary-stats">
            <div className="task-summary-stat">
              <p className="task-summary-title">{translate('Total Tasks')}</p>
              <p className="task-summary-number">{tasksTotal}</p>
            </div>

            {waitingTotal > 0 ? (
              <div className="task-summary-stat">
                <p className="task-summary-title">{translate('Waiting')}</p>
                <p className="task-summary-number">{waitingTotal}</p>
              </div>
            ) : null}

            {readyTotal > 0 ? (
              <div className="task-summary-stat">
                <p className="task-summary-title">{translate('Ready to Implement')}</p>
                <p className="task-summary-number">{readyTotal}</p>
              </div>
            ) : null}

            {impedimentTotal > 0 ? (
              <div className="task-summary-stat">
                <p className="task-summary-title">{translate('Technical Impediment')}</p>
                <p className="task-summary-number">{impedimentTotal}</p>
              </div>
            ) : null}

            {developmentTotal > 0 ? (
              <div className="task-summary-stat">
                <p className="task-summary-title">{translate('Under Development')}</p>
                <p className="task-summary-number">{developmentTotal}</p>
              </div>
            ) : null}

            {implementedTotal > 0 ? (
              <div className="task-summary-stat">
                <p className="task-summary-title">{translate('Implemented')}</p>
                <p className="task-summary-number">{implementedTotal}</p>
              </div>
            ) : null}
          </div>
          <div className="task-summary-btns">
            {canOpenDrive() && (
              <Button
                className="btn-drive"
                onClick={() => window.open(gDriveContentLink, '_blank')}
              >
                <GDriveIcon />
                {`${translate('Open')} Drive`}
              </Button>
            )}
            {canDownloadTaskCSV() && (
              <Button className="btn-download-csv" onClick={handleDownloadCSV}>
                {isLoading ? (
                  <MoonLoader size={20} color="#2d3748" loading={isLoading} />
                ) : (
                  <>
                    <i className="bx bx-download" />
                    {translate('Export CSV')}
                  </>
                )}
              </Button>
            )}
            <ButtonFilter handleToggle={handleToggle} filterTotal={filterTotal} />
            {canCreate() && (
              <Button className="btn-guideline-create" onClick={() => navigate('/tasks-create')}>
                {translate('Create Task')}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.99834 2C3.99834 3.47337 2.80394 4.66778 1.33057 4.66778C2.80394 4.66778 3.99834 5.86218 3.99834 7.33556C3.99834 5.86218 5.19275 4.66778 6.66612 4.66778C5.19275 4.66778 3.99834 3.47337 3.99834 2Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.3347 10.0054C11.3347 8.16367 9.84172 6.67066 8 6.67066C9.84172 6.67066 11.3347 5.17765 11.3347 3.33594C11.3347 5.17765 12.8277 6.67066 14.6694 6.67066C12.8277 6.67066 11.3347 8.16367 11.3347 10.0054Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.66607 9C6.66607 10.4734 5.47166 11.6678 3.99829 11.6678C5.47166 11.6678 6.66607 12.8622 6.66607 14.3356C6.66607 12.8622 7.86047 11.6678 9.33385 11.6678C7.86047 11.6678 6.66607 10.4734 6.66607 9Z"
                    fill="white"
                  />
                </svg>
              </Button>
            )}
          </div>
        </div>
      )}
    </TaskSummmaryLayout>
  );
};

TasksSummary.propTypes = {
  board: PropTypes.object,
  toggleFilter: PropTypes.func,
  show: PropTypes.bool,
  isContent: PropTypes.bool,
  isGuideline: PropTypes.bool,
  filterTotal: PropTypes.func,
  userStatus: PropTypes.string,
};

export default TasksSummary;
